/**
 * One person from the People table, or the "Everyone else" row behind it.
 *
 * A person: their figures from the table, then "Templates used", their column
 * of the request grid (/billing/request-breakdown), counted over the same
 * requests and period as the table, so the templates add up to their Requests.
 *
 * Everyone else: the people the table's top ten leaves out, each with their
 * requests; picking one shows their templates, with a way back to the list.
 */
import { useEffect, useState } from 'react'
import { AppWindow, Users, UserX } from 'lucide-react'
import UserAvatar from '../UserAvatar'
import DrawerShell, { DrawerStats } from './DrawerShell'
import BreakdownList from './BreakdownList'
import { formatInt } from '../../utils/billing'
import { everyoneElse, isServiceAccount, personDetail, personName, templatesUsed } from '../../utils/requestBreakdown'
import { isAllTimeLabel } from './useBillingData'

/** The People table's row in the grid's terms (id -> user_id), so one set of labels serves both. */
function asGridUser(row) {
  const id = row.user_id ?? row.id ?? null
  return {
    user_id: id,
    username: row.username,
    email: row.email,
    is_active: row.is_active,
    kind: row.kind || (id === null ? 'unknown' : isServiceAccount(row.email) ? 'service' : 'person'),
  }
}

function Mark({ user }) {
  const shape = 'w-10 h-10 flex-shrink-0 rounded-full flex items-center justify-center'
  if (user.kind === 'service') {
    return <span className={`${shape} bg-navy-50 dark:bg-navy-900/40 text-navy-600 dark:text-navy-300`}><AppWindow className="w-4 h-4" /></span>
  }
  if (user.kind === 'unknown') {
    return <span className={`${shape} bg-gray-100 dark:bg-gray-800 text-gray-400`}><UserX className="w-4 h-4" /></span>
  }
  return <UserAvatar user={{ id: user.user_id, username: user.username, email: user.email }} size="md" />
}

/**
 * @param target null (closed) | { type: 'person', row } | { type: 'others', listedIds, others }
 *   `row` is a People-table row (id, username, email, requests, documents,
 *   billable_pages, sharePct); `others` the table's "Everyone else" figures.
 */
export default function PersonDrawer({ target, breakdown, periodLabel, onClose, onRetryBreakdown }) {
  const [picked, setPicked] = useState(null)       // a person chosen from "Everyone else"
  useEffect(() => { setPicked(null) }, [target])

  const open = Boolean(target)
  const grid = breakdown?.data
  const period = isAllTimeLabel(periodLabel) ? 'All time' : `Last ${periodLabel}`

  if (open && target.type === 'others' && !picked) {
    const rest = everyoneElse(grid, target.listedIds)
    return (
      <DrawerShell open onClose={onClose} ariaLabel="Everyone else" eyebrow="People"
        title="Everyone else" subtitle={`Everyone not in the top list · ${period}`}
        leading={<span className="w-10 h-10 flex-shrink-0 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center
                                  justify-center text-gray-500 dark:text-gray-400"><Users className="w-4 h-4" /></span>}>
        <DrawerStats stats={[
          { label: 'Requests', value: formatInt(target.others?.requests || 0) },
          { label: 'Documents', value: formatInt(target.others?.documents || 0) },
          { label: 'People', value: breakdown?.loading || breakdown?.error ? '–' : formatInt(rest.rows.length) },
        ]} />
        <BreakdownList
          title="People"
          kind="people"
          rows={rest.rows}
          total={rest.total}
          expectedTotal={target.others?.requests ?? null}
          loading={breakdown?.loading}
          error={breakdown?.error}
          onRetry={onRetryBreakdown}
          onSelect={setPicked}
          emptyText="Nobody else raised requests in the period."
        />
      </DrawerShell>
    )
  }

  const row = picked || (open ? target.row : null)
  const user = row ? asGridUser(row) : null
  const used = user ? templatesUsed(grid, user.user_id) : { total: 0, rows: [] }
  // A person picked from "Everyone else" has only the grid's figures.
  const stats = picked
    ? [
        { label: 'Requests', value: formatInt(picked.requests) },
        { label: 'Templates', value: formatInt(used.rows.length) },
        { label: 'Of the rest', value: `${picked.sharePct}%` },
      ]
    : row
      ? [
          { label: 'Requests', value: formatInt(row.requests || 0) },
          { label: 'Documents', value: formatInt(row.documents || 0) },
          ...(row.billable_pages !== undefined
            ? [{ label: 'Billable pages', value: row.billable_pages === null ? '–' : formatInt(row.billable_pages) }]
            : []),
          ...(row.sharePct !== undefined ? [{ label: 'Share of requests', value: `${row.sharePct}%` }] : []),
        ]
      : []

  return (
    <DrawerShell
      open={open}
      onClose={onClose}
      onBack={picked ? () => setPicked(null) : null}
      ariaLabel={user ? `${personName(user)} details` : 'Person details'}
      eyebrow={user?.kind === 'service' ? 'App sign-in' : 'Person'}
      title={user ? personName(user) : ''}
      subtitle={user ? `${personDetail(user)}${personDetail(user) ? ' · ' : ''}${period}` : ''}
      leading={user ? <Mark user={user} /> : null}
    >
      {row && (
        <>
          <DrawerStats stats={stats} />
          <BreakdownList
            title="Templates used"
            kind="templates"
            rows={used.rows}
            total={used.total}
            expectedTotal={row.requests ?? null}
            loading={breakdown?.loading}
            error={breakdown?.error}
            onRetry={onRetryBreakdown}
            emptyText="No requests in the period."
          />
          <p className="text-[11px] text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-gray-800 pt-3">
            Each request is credited to whoever raised it, on the template it was raised against.
            {user?.kind === 'service' && ' Requests sent by an app (the Outlook add-in or an API client) sign in as the app, so the person behind them is not recorded on the request.'}
          </p>
        </>
      )}
    </DrawerShell>
  )
}
